import { cva, type VariantProps } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";

import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

/*
 * Geist's Button. Its label sits in a span with 6px of padding inside a
 * button with `--x-padding` (6/10/14px), so text is inset 12/16/20px; here
 * that 6px is folded into the button's own padding so that `asChild` links
 * and shadcn-style `<Icon />Label` children get the same geometry without a
 * wrapper. Icons beside a label are 14px (`--ds-control-decoration-size`);
 * an icon-only button's glyph is 16px.
 *
 * Disabled (and loading) wins over every variant with `!`: Geist paints all
 * of them the same grey, and hover must not show through.
 */
const buttonVariants = cva(
  [
    "relative inline-flex max-w-full shrink-0 cursor-pointer items-center justify-center font-medium whitespace-nowrap select-none outline-none",
    "transition-[border-color,background-color,color,box-shadow] duration-150 ease-in-out",
    "focus-visible:shadow-focus-ring focus-visible:transition-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "disabled:cursor-not-allowed disabled:bg-gray-100! disabled:text-gray-700! disabled:shadow-control-solid!",
    "aria-disabled:cursor-not-allowed aria-disabled:bg-gray-100! aria-disabled:text-gray-700! aria-disabled:shadow-control-solid!",
  ],
  {
    variants: {
      // A menu trigger stays in its hover colour while its menu is open, as
      // Geist's MenuButton does; a tertiary one (the dots menu) takes the
      // lighter gray-alpha-100.
      variant: {
        default:
          "bg-gray-1000 text-background-100 hover:bg-[#383838] data-[state=open]:bg-[#383838] dark:data-[state=open]:bg-[#ccc] dark:hover:bg-[#ccc]",
        secondary:
          "bg-background-100 text-gray-1000 shadow-control-solid hover:bg-gray-100 data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-200 dark:hover:bg-gray-200",
        tertiary:
          "bg-transparent text-gray-1000 hover:bg-gray-alpha-200 data-[state=open]:bg-gray-alpha-100",
        error: "bg-red-800 text-[#f5f5f5] hover:bg-[#ae292f] dark:text-white dark:hover:bg-red-900",
        warning: "bg-amber-800 text-[#0a0a0a] hover:bg-[#d27504]",
        // Not a Geist button: an inline text action, in Geist's link colour.
        link: "h-auto! bg-transparent px-0! text-[var(--ds-blue-700)] underline-offset-2 hover:underline dark:text-blue-900",
      },
      size: {
        tiny: "h-6 gap-1.5 rounded-sm px-2 text-button-12 [&_svg:not([class*='size-'])]:size-3",
        small: "h-8 gap-3 rounded-md px-3 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        medium: "h-9 gap-3 rounded-md px-4 text-sm [&_svg:not([class*='size-'])]:size-3.5",
        large: "h-10 gap-3 rounded-lg px-5 text-base [&_svg:not([class*='size-'])]:size-3.5",
      },
      shape: {
        default: "",
        square: "",
        circle: "rounded-full",
        // Geist's pill: 12px + the label's 6px on every size.
        rounded: "rounded-full px-[18px]",
      },
      svgOnly: {
        true: "px-0 [&_svg:not([class*='size-'])]:size-4",
        false: [
          // A leading icon sits 3px proud of the text inset, a trailing one
          // 4px, and a trailing icon is 10px from the text rather than 12.
          "[&>svg:first-child:not(:last-child)]:-ml-[3px]",
          "[&>svg:last-child:not(:first-child)]:-mr-1 [&>svg:last-child:not(:first-child)]:-ml-0.5",
        ],
      },
    },
    compoundVariants: [
      { svgOnly: true, size: "tiny", className: "size-6 rounded-sm" },
      { svgOnly: true, size: "small", className: "size-8" },
      { svgOnly: true, size: "medium", className: "size-9" },
      { svgOnly: true, size: "large", className: "size-10" },
      { svgOnly: true, shape: ["circle", "rounded"], className: "rounded-full px-0" },
      {
        variant: "tertiary",
        className:
          "disabled:bg-transparent! disabled:text-gray-1000! disabled:opacity-50 disabled:shadow-none! aria-disabled:bg-transparent! aria-disabled:text-gray-1000! aria-disabled:opacity-50 aria-disabled:shadow-none!",
      },
    ],
    defaultVariants: {
      variant: "default",
      size: "medium",
      shape: "default",
      svgOnly: false,
    },
  },
);

type GeistVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;
type GeistSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

/** shadcn's names, kept so existing call sites read as before. */
type LegacyVariant = "outline" | "ghost" | "destructive";
type LegacySize = "default" | "xs" | "sm" | "lg" | "icon" | "icon-xs" | "icon-sm" | "icon-lg";

type ButtonVariant = GeistVariant | LegacyVariant;
type ButtonSize = GeistSize | LegacySize;

const VARIANT: Record<ButtonVariant, GeistVariant> = {
  default: "default",
  secondary: "secondary",
  tertiary: "tertiary",
  error: "error",
  warning: "warning",
  link: "link",
  outline: "secondary",
  ghost: "tertiary",
  destructive: "error",
};

const SIZE: Record<ButtonSize, { size: GeistSize; svgOnly?: true }> = {
  tiny: { size: "tiny" },
  small: { size: "small" },
  medium: { size: "medium" },
  large: { size: "large" },
  default: { size: "medium" },
  xs: { size: "tiny" },
  sm: { size: "small" },
  lg: { size: "large" },
  icon: { size: "medium", svgOnly: true },
  "icon-xs": { size: "tiny", svgOnly: true },
  "icon-sm": { size: "small", svgOnly: true },
  "icon-lg": { size: "large", svgOnly: true },
};

const SPINNER = { tiny: "sm", small: "sm", medium: "md", large: "lg" } as const;

type ButtonProps = Omit<React.ComponentProps<"button">, "prefix"> & {
  variant?: ButtonVariant | null;
  size?: ButtonSize | null;
  /** `square` and `circle` are for icon-only buttons; `rounded` is the pill. */
  shape?: "square" | "circle" | "rounded" | null;
  /** Icon-only: a square the button's height. Implied by the `icon*` sizes. */
  svgOnly?: boolean;
  asChild?: boolean;
  /** Leading icon, in Geist's 20px slot. */
  prefix?: React.ReactNode;
  /** Trailing icon, e.g. a chevron or an external-link arrow. */
  suffix?: React.ReactNode;
  /** Swaps the prefix (or an icon-only button's icon) for a spinner and disables the button. */
  loading?: boolean;
};

function Button({
  className,
  variant,
  size,
  shape,
  svgOnly,
  asChild = false,
  prefix,
  suffix,
  loading = false,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const resolved = SIZE[size ?? "default"] ?? SIZE.default;
  const iconOnly = svgOnly ?? resolved.svgOnly ?? false;
  const geistVariant = VARIANT[variant ?? "default"] ?? "default";
  const spinner = loading ? (
    <Spinner size={SPINNER[resolved.size]} className="text-current" />
  ) : null;

  const lead = spinner && !iconOnly ? spinner : prefix;
  const content = spinner && iconOnly ? spinner : children;

  const Comp = asChild ? Slot.Root : "button";
  const Content = asChild ? Slot.Slottable : React.Fragment;

  return (
    <Comp
      data-slot="button"
      data-variant={geistVariant}
      data-size={resolved.size}
      aria-busy={loading || undefined}
      disabled={asChild ? undefined : disabled || loading}
      aria-disabled={asChild && (disabled || loading) ? true : undefined}
      className={cn(
        buttonVariants({
          variant: geistVariant,
          size: resolved.size,
          shape: shape ?? "default",
          svgOnly: iconOnly,
        }),
        className,
      )}
      {...props}
    >
      {lead != null && (
        <span
          data-slot="button-prefix"
          className="-mr-[3px] -ml-1.5 flex min-w-5 shrink-0 items-center justify-center"
        >
          {lead}
        </span>
      )}
      <Content>{content}</Content>
      {suffix != null && (
        <span data-slot="button-suffix" className="-mr-1 -ml-0.5 flex shrink-0 items-center">
          {suffix}
        </span>
      )}
    </Comp>
  );
}

export { Button, type ButtonProps, buttonVariants };
